import { useLayoutEffect, useMemo, useRef, useState } from "react";

const MIN_LABEL_COLUMN_WIDTH = 150;
const MIN_BRAND_COLUMN_WIDTH = 100;
const LABEL_COLUMN_PADDING = 40;
const BRAND_COLUMN_PADDING = 40;
const LABEL_FONT = "700 14px Roboto, Helvetica, Arial, sans-serif";
const BRAND_HEADER_FONT = "700 14px Roboto, Helvetica, Arial, sans-serif";
const BRAND_CELL_FONT = "400 14px Roboto, Helvetica, Arial, sans-serif";

// Every non-numeric state a metric cell can render (see ScorecardChart's
// renderMetricCell) — the widest of these, not just the header, sets a
// brand column's natural minimum so a long state label never truncates.
const BRAND_CELL_STATE_SAMPLES = ["Base Unresolved", "Unresolved", "Suppressed", "100.00%"];

let measurementCanvas: HTMLCanvasElement | null = null;

function measureTextWidth(text: string, font: string): number {
  if (!measurementCanvas) measurementCanvas = document.createElement("canvas");
  const context = measurementCanvas.getContext("2d");
  if (!context) return text.length * 8; // non-canvas environments (e.g. jsdom in tests)
  context.font = font;
  return context.measureText(text).width;
}

interface ScorecardColumnWidthsInput {
  metricColumnHeader: string;
  /** Non-heading row labels only — Heading rows render full-width and
   *  don't factor into column width allocation. */
  rowLabels: string[];
  /** Visible brands only, in display order — hidden brands don't factor
   *  into width allocation. */
  brands: { id: string; label: string }[];
}

interface ScorecardColumnWidthsResult {
  containerRef: React.RefObject<HTMLDivElement | null>;
  labelColumnWidth: number;
  brandColumnWidths: Record<string, number>;
}

/**
 * Computes automatic column widths for the Scorecard table: the first
 * (metric/label) column fits its header and row labels up to one third of
 * the container width, and remaining width is distributed equally across
 * visible brand columns (never below what their header/value/state text
 * needs). Falls back to natural (un-clamped) sizes before the container has
 * been measured — MUI DataGrid's own horizontal scroll takes over when the
 * sum of natural widths exceeds the container.
 */
export function useScorecardColumnWidths({
  metricColumnHeader,
  rowLabels,
  brands,
}: ScorecardColumnWidthsInput): ScorecardColumnWidthsResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const updateWidth = () => setContainerWidth(element.clientWidth);
    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, []);

  return useMemo(() => {
    const header = metricColumnHeader.trim() || "Metric";
    const naturalLabelWidth =
      Math.ceil(
        Math.max(
          measureTextWidth(header, LABEL_FONT),
          ...rowLabels.map((label) => measureTextWidth(label, LABEL_FONT)),
        ),
      ) + LABEL_COLUMN_PADDING;

    const naturalBrandWidths = brands.map((brand) => {
      const headerWidth = measureTextWidth(brand.label, BRAND_HEADER_FONT);
      const cellWidth = Math.max(
        ...BRAND_CELL_STATE_SAMPLES.map((sample) => measureTextWidth(sample, BRAND_CELL_FONT)),
      );
      return Math.max(
        MIN_BRAND_COLUMN_WIDTH,
        Math.ceil(Math.max(headerWidth, cellWidth)) + BRAND_COLUMN_PADDING,
      );
    });

    if (containerWidth === null) {
      const brandColumnWidths: Record<string, number> = {};
      brands.forEach((brand, index) => {
        brandColumnWidths[brand.id] = naturalBrandWidths[index];
      });
      return {
        containerRef,
        labelColumnWidth: Math.max(MIN_LABEL_COLUMN_WIDTH, naturalLabelWidth),
        brandColumnWidths,
      };
    }

    const labelColumnWidth = Math.max(
      MIN_LABEL_COLUMN_WIDTH,
      Math.min(naturalLabelWidth, Math.floor(containerWidth / 3)),
    );

    const remainingWidth = Math.max(0, containerWidth - labelColumnWidth);
    const equalShare = brands.length > 0 ? remainingWidth / brands.length : 0;

    const brandColumnWidths: Record<string, number> = {};
    brands.forEach((brand, index) => {
      brandColumnWidths[brand.id] = Math.round(
        Math.max(naturalBrandWidths[index], equalShare),
      );
    });

    return { containerRef, labelColumnWidth, brandColumnWidths };
  }, [metricColumnHeader, rowLabels, brands, containerWidth]);
}
